/**
 * doveTheme — the Subcontractors module's navy-and-gold skin, and the settings
 * that drive it (Mikko, 2026-10-11: sampled from a Dove pack — the blue panel,
 * the gold "x6" badge, the navy of "for soft, moisturized skin" — "we will try
 * this first in the Subcontractors module" and "include it in settings so I can
 * change it immediately").
 *
 * Every colour is a setting, validated as a hex code, kept in this browser's
 * localStorage and applied the moment it changes. Nothing is stored in
 * SharePoint, so trying it costs no list and no Migration Valve step; the price
 * is that it is per browser. The side navigation wears it in EVERY module
 * (`html[data-dove]`, see styles.css, set from the Shell since 2026-10-11); the
 * ink applies to the light mode of the payroll screens only.
 *
 * Import-free on purpose, so it is testable without the app's env vars.
 */
export interface IDoveTheme {
  /** Master switch — off restores the module's previous look exactly. */
  on: boolean;
  /** Side navigation fill (the pack's blue panel). */
  navBg: string;
  /** Text on the navigation: items, section titles. */
  navText: string;
  /** Tint of the glass on Back/Home and on the open and hovered items. */
  accent: string;
  /** Text on that gold fill. */
  accentText: string;
  /** Text ink on the light screens: headers, tabs, table values. */
  ink: string;
}

export const DEFAULT_DOVE: IDoveTheme = {
  on: true,
  navBg: "#101B35",
  navText: "#FFFFFF",
  accent: "#525152",
  accentText: "#FFFFFF",
  ink: "#001E52",
};

export const DOVE_FIELDS: { key: Exclude<keyof IDoveTheme, "on">; label: string; hint: string }[] = [
  { key: "navBg", label: "Navigation fill", hint: "The side navigation bar's background." },
  { key: "navText", label: "Navigation text", hint: "Item names and section titles in the navigation." },
  { key: "accent", label: "Glass tint", hint: "The tint of the frosted glass on Back, Home and the navigation items." },
  { key: "accentText", label: "Text on glass", hint: "The words on Back, Home and the open item." },
  { key: "ink", label: "Ink", hint: "Headers, tabs and table values on the light screens." },
];

export const DOVE_KEY = "ish:subcon-dove";

/** `#rgb` or `#rrggbb`, returned as upper-case `#RRGGBB`; anything else null. */
export function normaliseHex(raw: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((raw ?? "").trim());
  if (!m) return null;
  let h = m[1].toUpperCase();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return `#${h}`;
}

/** A stored value, any shape, to a usable theme: bad fields fall back one by one. */
export function parseDove(raw: string | null | undefined): IDoveTheme {
  let o: Record<string, unknown> = {};
  try { const v = JSON.parse(raw ?? ""); if (v && typeof v === "object") o = v as Record<string, unknown>; } catch { /* default */ }
  const out: IDoveTheme = { ...DEFAULT_DOVE };
  for (const f of DOVE_FIELDS) {
    const h = typeof o[f.key] === "string" ? normaliseHex(o[f.key] as string) : null;
    if (h) out[f.key] = h;
  }
  if (typeof o.on === "boolean") out.on = o.on;
  return out;
}

/** The CSS custom properties the stylesheet reads, on the document element. */
export function doveCssVars(t: IDoveTheme): Record<string, string> {
  return {
    "--dv-nav-bg": t.navBg,
    "--dv-nav-text": t.navText,
    "--dv-accent": t.accent,
    "--dv-accent-text": t.accentText,
  };
}

/** Light-mode ink for the payroll screens; empty when off, so nothing moves. */
export function doveInkVars(t: IDoveTheme): Record<string, string> {
  return t.on ? { "--sub-heading": t.ink, "--sub-text": t.ink } : {};
}

let current: IDoveTheme | null = null;
const listeners = new Set<() => void>();

function load(): IDoveTheme {
  try { return parseDove(localStorage.getItem(DOVE_KEY)); } catch { return { ...DEFAULT_DOVE }; }
}
export function getDove(): IDoveTheme {
  if (current === null) current = load();
  return current;
}
export function subscribeDove(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
/** Change one or more settings; invalid hex is ignored, the rest applies at once. */
export function setDove(patch: Partial<IDoveTheme>): void {
  const next = { ...getDove() };
  for (const f of DOVE_FIELDS) {
    const v = patch[f.key];
    if (typeof v === "string") { const h = normaliseHex(v); if (h) next[f.key] = h; }
  }
  if (typeof patch.on === "boolean") next.on = patch.on;
  current = next;
  try { localStorage.setItem(DOVE_KEY, JSON.stringify(next)); } catch { /* per-tab only */ }
  for (const l of listeners) l();
}
export function resetDove(): void {
  current = { ...DEFAULT_DOVE };
  try { localStorage.removeItem(DOVE_KEY); } catch { /* ignore */ }
  for (const l of listeners) l();
}
